import { useStore, useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useEffect, useMemo, useState } from 'react';

import type { Speaker } from '../content-components/SpeakerTypes';

type ClipTheme = { title: string; speakerLabels?: string[] };

export type InputOptions = {
  enableSpeakerSelection?: boolean;
};

export type ReturnType = {
  // presets
  selectedPresets: string[];
  setSelectedPresets: (presets: string[]) => void;
  onPresetClick: (preset: string) => void;

  // themes
  suggestedThemes: string[];
  selectedSuggestedTheme: string | null;
  onSuggestedThemeClick: (title: string) => void;

  // speakers
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  setSelectedSpeakers: (speakers: Speaker[]) => void;
  isLoadingSpeakers: boolean;
  showSpeakerSelector: boolean;
  useSpeakerFilter: boolean;
  setUseSpeakerFilter: (useFilter: boolean) => void;
};

export function useAIClipProducerTheme(options?: InputOptions): ReturnType {
  const videoCreator = useVideoCreatorStore();
  const transcriptionStore = useTranscription();
  const { userIdentityStore } = useStore();
  const flags = useFlagsCombination(
    userIdentityStore.currentIdentity?.role as any,
  );

  // Preset selection
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);

  // Themes (suggested topics)
  const [allSuggestedThemes, setAllSuggestedThemes] = useState<ClipTheme[]>([]);
  const [selectedSuggestedTheme, setSelectedSuggestedTheme] = useState<
    string | null
  >(null);

  // Speaker selection state
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
  const [isLoadingSpeakers, setIsLoadingSpeakers] = useState(false);
  const [useSpeakerFilter, setUseSpeakerFilter] = useState(false);

  const isTranscriptEditorEnabled = flags.enableTranscriptEditor;
  const showSpeakerSelector = Boolean(
    (options?.enableSpeakerSelection ?? false) && isTranscriptEditorEnabled,
  );

  // Load themes from story.clipThemesJson when enabled
  useEffect(() => {
    const raw = flags.enableClipThemeGeneration
      ? ((videoCreator.story as any)?.clipThemesJson as unknown)
      : undefined;
    const themes = parseClipThemesJson(raw);
    setAllSuggestedThemes(themes);
    if (themes.length === 0) {
      setSelectedSuggestedTheme(null);
    }
  }, [videoCreator.story?.id, flags.enableClipThemeGeneration]);

  // Load speakers when selection enabled and story present
  useEffect(() => {
    if (!showSpeakerSelector || !videoCreator.story?.id) return;

    let cancelled = false;
    const loadSpeakers = async () => {
      setIsLoadingSpeakers(true);
      try {
        const transcriptElements =
          transcriptionStore.getFinalTranscriptionElements();
        const speakerLabels =
          extractSpeakerLabelsFromTranscriptElements(transcriptElements);

        if (speakerLabels.length === 0) {
          if (!cancelled) setIsLoadingSpeakers(false);
          return;
        }

        const [speakerMappings, datoSpeakers] = await Promise.all([
          fetchSpeakerVoiceMappings(videoCreator.story!.id),
          fetchDatoSpeakers(
            videoCreator.story!.id,
            videoCreator.datoClientStore,
          ),
        ]);

        if (cancelled) return;
        const speakerList = buildSpeakersFromMappings(
          speakerLabels,
          speakerMappings,
          datoSpeakers,
        );

        setSpeakers(speakerList);
        setSelectedSpeakers(speakerList);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading speaker data:', error);
      } finally {
        if (!cancelled) setIsLoadingSpeakers(false);
      }
    };

    loadSpeakers();
    return () => {
      cancelled = true;
    };
  }, [videoCreator.story?.id, showSpeakerSelector]);

  // Interaction: when a theme with speakerLabels is selected and filter is on,
  // select speakers associated with that theme
  useEffect(() => {
    if (!useSpeakerFilter || !selectedSuggestedTheme) return;
    const theme = allSuggestedThemes.find(
      t => t.title === selectedSuggestedTheme,
    );
    const labels = theme?.speakerLabels;
    if (!labels || labels.length === 0) return;
    const allowed = new Set(labels);
    setSelectedSpeakers(prev => {
      const base = speakers.length ? speakers : prev;
      return base.filter(s => allowed.has(s.speakerLabel));
    });
  }, [useSpeakerFilter, selectedSuggestedTheme, allSuggestedThemes, speakers]);

  // Handlers coordinating presets <-> theme selection
  const onSuggestedThemeClick = (title: string) => {
    setSelectedSuggestedTheme(prev => {
      const next = prev === title ? null : title;
      if (next) setSelectedPresets([]);
      return next;
    });
  };

  const onPresetClick = (preset: string) => {
    setSelectedPresets(prev => {
      if (prev.includes(preset)) {
        return prev.filter(p => p !== preset);
      }
      setSelectedSuggestedTheme(null);
      return [preset];
    });
  };

  const suggestedThemes = useMemo(
    () => allSuggestedThemes.map(t => t.title),
    [allSuggestedThemes],
  );

  return {
    // presets
    selectedPresets,
    setSelectedPresets,
    onPresetClick,

    // themes
    suggestedThemes,
    selectedSuggestedTheme,
    onSuggestedThemeClick,

    // speakers
    speakers,
    selectedSpeakers,
    setSelectedSpeakers,
    isLoadingSpeakers,
    showSpeakerSelector,
    useSpeakerFilter,
    setUseSpeakerFilter,
  } as const;
}

function parseClipThemesJson(raw: unknown): ClipTheme[] {
  try {
    if (!raw) return [];
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as any);
    const themesArray: any[] = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.themes)
        ? parsed.themes
        : [];
    const objs: ClipTheme[] = [];
    const seenTitles = new Set<string>();
    for (const t of themesArray) {
      const title = (typeof t === 'string' ? t : t?.title) as
        | string
        | undefined;
      if (!title || !title.trim()) continue;
      const normTitle = title.trim();
      if (seenTitles.has(normTitle)) continue;
      seenTitles.add(normTitle);
      const speakerLabels: string[] | undefined =
        (typeof t === 'object' && (t?.speakerLabels || t?.speaker_labels)) ||
        undefined;
      objs.push({ title: normTitle, speakerLabels });
    }
    return objs;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to parse story.clipThemesJson', e);
    return [];
  }
}
